/** The theme on screen. */
export type SMLTheme = 'dark' | 'light';

/**
 * The choice of the user. `system` follows the device color scheme (`prefers-color-scheme`), also
 * live. music24.com uses the same storage key and the same three values, so keep them exactly.
 */
export type SMLThemePreference = 'system' | 'light' | 'dark';

export const SML_THEME_STORAGE_KEY = 'music24-theme';

// The choice for a user with no stored value or an unknown stored value. Only a click in the
// switch writes the storage key (`SMLThemeProvider.setPreference`, and the logout restore of a
// stored value).
export const DEFAULT_SML_THEME_PREFERENCE: SMLThemePreference = 'system';

// The theme when the device color scheme is unknown (no `matchMedia`).
export const DEFAULT_SML_THEME: SMLTheme = 'light';

export const SML_THEME_PREFERENCES: readonly SMLThemePreference[] = ['system', 'light', 'dark'];

const isSMLThemePreference = (value: unknown): value is SMLThemePreference =>
  value === 'system' || value === 'dark' || value === 'light';

/** A stored value as a choice, or null when it is not one of the three values. */
export function parseSMLThemePreference(value: unknown): SMLThemePreference | null {
  return isSMLThemePreference(value) ? value : null;
}

/** The stored choice, or null when no valid choice is stored or the storage is blocked. */
export function getStoredSMLThemePreference(): SMLThemePreference | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const stored = localStorage.getItem(SML_THEME_STORAGE_KEY);
    return isSMLThemePreference(stored) ? stored : null;
  } catch (error) {
    console.warn('Could not read Music24 theme from storage', error);
    return null;
  }
}

export function storeSMLThemePreference(preference: SMLThemePreference): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SML_THEME_STORAGE_KEY, preference);
  } catch (error) {
    console.warn('Could not persist Music24 theme to storage', error);
  }
}

/** The stored choice, else `system`. */
export function getInitialSMLThemePreference(): SMLThemePreference {
  return getStoredSMLThemePreference() ?? DEFAULT_SML_THEME_PREFERENCE;
}

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

function darkSchemeQuery(): MediaQueryList | null {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return null;
    }
    return window.matchMedia(DARK_SCHEME_QUERY);
  } catch (error) {
    console.warn('Could not read the device color scheme', error);
    return null;
  }
}

/** The color scheme of the device (`prefers-color-scheme`). Light when it is unknown. */
export function getSystemSMLTheme(): SMLTheme {
  const query = darkSchemeQuery();
  if (query === null) {
    return DEFAULT_SML_THEME;
  }
  return query.matches ? 'dark' : 'light';
}

/** The theme on screen for a choice: `system` reads the device color scheme. */
export function resolveSMLTheme(preference: SMLThemePreference): SMLTheme {
  return preference === 'system' ? getSystemSMLTheme() : preference;
}

/**
 * Calls `onChange` with the new theme when the device color scheme changes. Returns the function
 * that stops the calls. Does nothing when the browser has no `matchMedia`.
 */
export function subscribeToSystemSMLTheme(onChange: (theme: SMLTheme) => void): () => void {
  const query = darkSchemeQuery();
  if (query === null) {
    return () => undefined;
  }
  const listener = (event: MediaQueryListEvent): void => onChange(event.matches ? 'dark' : 'light');
  // Safari before 14 has only the old `addListener` on a MediaQueryList.
  if (typeof query.addEventListener !== 'function') {
    query.addListener(listener);
    return () => query.removeListener(listener);
  }
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}

/** The theme on screen at start: the stored choice, else the device color scheme. */
export function getInitialSMLTheme(): SMLTheme {
  return resolveSMLTheme(getInitialSMLThemePreference());
}

/**
 * Applies the theme on screen to the document root. Sets both `data-theme` (drives the
 * design-language CSS tokens) and `data-bs-theme` (drives Bootstrap's own component variants), so
 * the two stay in sync. It always sets the resolved value (`light` or `dark`), never `system`.
 * No-op outside the browser.
 */
export function applySMLThemeToDocument(theme: SMLTheme): void {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-bs-theme', theme);
}
